import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
    getStaticText,
    MEANINGFUL_SHIFT_STANDARD_VAR,
    MEANINGFUL_SHIFT_HIGH_BAR_VAR,
} from '../../../../utils/constants';
import { getBenchmarksLabel, getDemographicsLabel, manipulateHeatmapTitle } from '../../../../utils/functions';
import { findIndExpHeatmapHeaderIndex } from '../indExperiencePublicSector';
import {
    DIFF_EXCEL_COLORS,
    ExcelFilterRows,
    encodeFillCell,
    formatScoreValue,
    getExcelFileName,
    getIeScoreCell,
    isNSizeTitle,
    writeColoredExcel,
} from '../excelUtils';

const TABLE_ID = 'ieHeatmapResurveyExcelTable';

function ExcelResurvey({
    headerToUse,
    dataToUse,
    header,
    practiceCategories,
    enpsHeatmap,
    enpsHeader,
    filters,
    selectedDemographic,
    defaultSettings,
    headerTranslationMap,
    years,
    comparisonType,
    setSelectedDownload,
}) {
    const { lang = '1033', ohid = '', apply_employee_exp_benchmark = false, quartileColors = [] } = defaultSettings;
    const staticText = getStaticText(lang);
    const { SITE_TEXT } = staticText;
    const {
        NEGATIVE_PRACTICES = [],
        EMPLOYEE_NET_PROMOTER_SCORE,
        HEATMAP,
        EX_ENPS_HEADER = [],
        DEMO_SELECTED_OPTIONS,
        BENCHMARK_FILTERS,
        NUMBER_TEXT,
    } = SITE_TEXT;
    const { demographics = [] } = filters[0] || {};
    const selectedGroup = demographics.find(({ code }) => code === selectedDemographic) || {};
    const selectedLabel = selectedGroup.label || '';
    const isMeaningfulShiftSetting =
        comparisonType &&
        (comparisonType.toLowerCase() === MEANINGFUL_SHIFT_STANDARD_VAR ||
            comparisonType.toLowerCase() === MEANINGFUL_SHIFT_HIGH_BAR_VAR);

    const getDiffCell = (diffValue, significance, isN) => {
        if (isN) return encodeFillCell('#ffffff', '#000000', '');
        const display = diffValue !== null && diffValue !== undefined && diffValue !== '' ? diffValue : '-';
        const diffClass = ['neg', 'pos', 'neutral'];
        const diffClassMeaningful = ['largeDec', 'smallDec', 'neutral-meaningful', 'smallInc', 'largeInc'];
        const key = isMeaningfulShiftSetting ? diffClassMeaningful[significance - 1] : diffClass[significance];
        const { bg = '#ffffff', color = '#000000' } = DIFF_EXCEL_COLORS[key] || {};
        return encodeFillCell(bg, color, display);
    };

    const getExport = () => {
        setSelectedDownload('');
        return writeColoredExcel(TABLE_ID, getExcelFileName('Employee_Experience_Heatmap_Resurvey', defaultSettings), {
            preventDateCoercion: true,
        });
    };

    useEffect(() => {
        getExport();
        // eslint-disable-next-line
    }, []);

    const renderHeatmapRows = () => {
        const rows = [];
        practiceCategories.forEach(({ children: child = [], header_slug: headerSlug = [] }) => {
            child.forEach((elem, childIndex) => {
                const index = findIndExpHeatmapHeaderIndex(elem, childIndex, headerSlug, header, headerTranslationMap);
                if (index === -1 || !dataToUse[index]) return;
                const title = manipulateHeatmapTitle(header[index]);
                const isNegativeTrait = NEGATIVE_PRACTICES.includes(title);
                const isN = isNSizeTitle(header[index]) || isNSizeTitle(title) || title === NUMBER_TEXT;
                const subItems = dataToUse[index] || [];
                rows.push(
                    <tr key={`hmr-${title}-${index}`}>
                        <td>bold#{title}</td>
                        {subItems.map((cell, cellIndex) => {
                            const { score = [], differences = [], significance = [], quartile = [] } = cell || {};
                            const val = Array.isArray(score) ? score[0] : score;
                            return (
                                <Fragment key={`${title}-${cellIndex}`}>
                                    <td>
                                        {getIeScoreCell({
                                            score: val,
                                            quartile: Array.isArray(quartile) ? quartile[0] : quartile,
                                            isNegativeTrait,
                                            isN,
                                            applyBenchmark: apply_employee_exp_benchmark,
                                            quartileColors,
                                            ohid,
                                        })}
                                    </td>
                                    <td>
                                        {getDiffCell(
                                            differences[0],
                                            Array.isArray(significance) ? significance[0] : significance,
                                            isN
                                        )}
                                    </td>
                                </Fragment>
                            );
                        })}
                    </tr>
                );
            });
        });
        return rows;
    };

    const renderEnpsRows = () => {
        if (!enpsHeatmap.length) return null;
        return (
            <>
                <tr>
                    <td>
                        bold#{EMPLOYEE_NET_PROMOTER_SCORE} (eNPS) {HEATMAP}
                    </td>
                </tr>
                <tr>
                    <th />
                    {enpsHeader.map((val, i) => (
                        <Fragment key={`enpsr-h-${val}-${i}`}>
                            <th>bold#{manipulateHeatmapTitle(val)}#center</th>
                            <th>bold#Difference#center</th>
                        </Fragment>
                    ))}
                </tr>
                {enpsHeatmap.map((subItems, parentI) => {
                    const title = EX_ENPS_HEADER[parentI] || '';
                    const isN = isNSizeTitle(title);
                    const isEnps = [1, 5].includes(parentI);
                    const skipEnpsFill = [2, 3, 4].includes(parentI);
                    return (
                        <tr key={`enpsr-${title}-${parentI}`}>
                            <td>bold#{title}</td>
                            {(subItems || []).map((cell, cellIndex) => {
                                const {
                                    score = [],
                                    differences = [],
                                    difference = [],
                                    significance = [],
                                    quartile = [],
                                } = cell || {};
                                const innerScore = Array.isArray(score) ? score[0] : score;
                                const quart = title === 'OHI Score' || title === 'eNPS' ? quartile[0] : 999;
                                const sig = Array.isArray(significance) ? significance[0] : significance;
                                const diff = typeof difference === 'number' ? difference : differences[0];
                                const emptyDiffValue = formatScoreValue(diff) === '-' ? '' : diff;
                                const useDiffColor = !skipEnpsFill && (isEnps || !isN);
                                const diffCell = useDiffColor
                                    ? getDiffCell(diff, sig, isN)
                                    : encodeFillCell('#ffffff', '#000000', emptyDiffValue);
                                return (
                                    <Fragment key={`${title}-${cellIndex}`}>
                                        <td>
                                            {getIeScoreCell({
                                                score: innerScore,
                                                quartile: quart,
                                                isN: isN || skipEnpsFill,
                                                applyBenchmark: true,
                                                quartileColors,
                                                forceQuartile: true,
                                            })}
                                        </td>
                                        <td>{diffCell}</td>
                                    </Fragment>
                                );
                            })}
                        </tr>
                    );
                })}
            </>
        );
    };

    return (
        <div>
            <table id={TABLE_ID} className="hideTable">
                <ExcelFilterRows
                    selectedLabel={selectedLabel}
                    years={years}
                    demoFilterLabelsByYear={filters.map(({ demographics: demo = [] }) => getDemographicsLabel(demo))}
                    benchLabelsByYear={filters.map(({ benchmarks: bm = [] }) => getBenchmarksLabel(bm))}
                    exBenchLabelsByYear={filters.map(({ ex_benchmarks: exBm = [] }) => getBenchmarksLabel(exBm))}
                    staticText={{ DEMO_SELECTED_OPTIONS, BENCHMARK_FILTERS }}
                />
                <tbody>
                    <tr>
                        <th />
                        {headerToUse.map((val, i) => (
                            <Fragment key={`h-${val}-${i}`}>
                                <th colSpan="2">bold#{manipulateHeatmapTitle(val)}#center</th>
                            </Fragment>
                        ))}
                    </tr>
                    <tr>
                        <th />
                        {headerToUse.map((val, i) => (
                            <Fragment key={`hy-${val}-${i}`}>
                                <th>bold#Score#center</th>
                                <th>bold#Difference#center</th>
                            </Fragment>
                        ))}
                    </tr>
                    {renderHeatmapRows()}
                    {renderEnpsRows()}
                </tbody>
            </table>
        </div>
    );
}

ExcelResurvey.propTypes = {
    headerToUse: PropTypes.array.isRequired,
    dataToUse: PropTypes.array.isRequired,
    header: PropTypes.array.isRequired,
    practiceCategories: PropTypes.array.isRequired,
    enpsHeatmap: PropTypes.array,
    enpsHeader: PropTypes.array,
    filters: PropTypes.array.isRequired,
    selectedDemographic: PropTypes.string.isRequired,
    defaultSettings: PropTypes.object.isRequired,
    headerTranslationMap: PropTypes.object,
    years: PropTypes.array.isRequired,
    comparisonType: PropTypes.string,
    setSelectedDownload: PropTypes.func.isRequired,
};

ExcelResurvey.defaultProps = {
    enpsHeatmap: [],
    enpsHeader: [],
    headerTranslationMap: {},
    comparisonType: '',
};

export default ExcelResurvey;
